/**
 * RechargeProvider — Round 40h (Round 40i: currency-aware)
 *
 * Single app-wide recharge sheet. Branches on user's currency:
 *   • INR → Razorpay Checkout (existing flow)
 *   • USD → PayPal Smart Buttons (new — Round 40i)
 *
 * Both branches go through the same `useRecharge().open()` hook so
 * consumer pages (Compatibility, BTR, Chat top-up surfaces) don't
 * need to know which payment provider is being used.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { readErr } from '@/lib/api';
import { fetchRegion, loadPaypalSdk, formatPrice } from '@/lib/region';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

const RechargeContext = createContext(null);

// Round 40o.2 — bonus-bracket fallbacks. Mirror backend
// WALLET_BONUS_BRACKETS so the "+₹X bonus" chip renders under each
// preset tile immediately on first paint, before /payments/wallet/tiers
// resolves, and even if the call 401s on an expired token.
const BONUS_FALLBACK_INR = [
    { min_inr: 100,  max_inr: 500,    bonus_pct: 15 },
    { min_inr: 501,  max_inr: 1000,   bonus_pct: 30 },
    { min_inr: 1001, max_inr: null,   bonus_pct: 50 },
];
const BONUS_FALLBACK_USD = [
    { min_usd: 10,  max_usd: 50,    pct: 15 },
    { min_usd: 51,  max_usd: 150,   pct: 30 },
    { min_usd: 151, max_usd: null,  pct: 50 },
];

function bonusPctFor(amount, brackets, currency) {
    if (!Array.isArray(brackets) || !amount) return 0;
    const key = currency === 'USD' ? 'min_usd' : 'min_inr';
    const maxKey = currency === 'USD' ? 'max_usd' : 'max_inr';
    for (const b of brackets) {
        const lo = Number(b[key] || 0);
        const hi = b[maxKey] == null ? null : Number(b[maxKey]);
        const pct = Number(b.pct ?? b.bonus_pct ?? 0);
        if (amount >= lo && (hi === null || amount <= hi)) return pct;
    }
    return 0;
}

export function RechargeProvider({ children }) {
    const [open, setOpen] = useState(false);
    const [region, setRegion] = useState({ currency: 'INR', symbol: '₹', provider: 'razorpay' });
    const [tiers, setTiers] = useState([25, 50, 100, 250, 500, 1000, 2500, 5000, 10000]);
    const [bonusBrackets, setBonusBrackets] = useState(BONUS_FALLBACK_INR);
    const [customAmount, setCustomAmount] = useState('');
    const [recharging, setRecharging] = useState(false);
    const [paypalConfig, setPaypalConfig] = useState(null);
    const onSuccessRef = useRef(null);

    // Resolve region once, then load currency-appropriate tiers.
    useEffect(() => {
        (async () => {
            const r = await fetchRegion();
            setRegion(r);
            // Round 40o.2 — flip presets+brackets to USD fallback when
            // region resolves international, so chips stay correct
            // before /payments/wallet/tiers resolves.
            if (r.currency === 'USD') {
                setTiers([3, 5, 10, 25, 50, 100, 125, 150, 175, 200]);
                setBonusBrackets(BONUS_FALLBACK_USD);
            }
            try {
                const { data } = await api.get(`/payments/wallet/tiers?currency=${r.currency}`);
                if (r.currency === 'USD') {
                    setTiers(Array.isArray(data.presets_usd) && data.presets_usd.length ? data.presets_usd : [3, 5, 10, 25, 50, 100, 125, 150, 175, 200]);
                    setBonusBrackets(Array.isArray(data.bonus_brackets_usd) && data.bonus_brackets_usd.length ? data.bonus_brackets_usd : BONUS_FALLBACK_USD);
                } else {
                    setTiers(Array.isArray(data.presets_inr) && data.presets_inr.length ? data.presets_inr : [25, 50, 100, 250, 500, 1000, 2500, 5000, 10000]);
                    setBonusBrackets(Array.isArray(data.bonus_brackets) && data.bonus_brackets.length ? data.bonus_brackets : BONUS_FALLBACK_INR);
                }
            } catch { /* keep fallbacks */ }
            if (r.currency === 'USD') {
                try {
                    const { data } = await api.get('/payments/paypal/config');
                    setPaypalConfig(data);
                } catch { /* sdk will be loaded on demand */ }
            }
        })();
    }, []);

    const openRecharge = useCallback((opts = {}) => {
        onSuccessRef.current = typeof opts.onSuccess === 'function' ? opts.onSuccess : null;
        setOpen(true);
    }, []);

    const closeRecharge = useCallback(() => {
        setOpen(false);
        onSuccessRef.current = null;
    }, []);

    // ── Razorpay (INR) branch — unchanged ──
    const doRazorpayRecharge = async (amountInr) => {
        if (!window.Razorpay) {
            toast.error('Payment SDK not loaded — please refresh and try again.');
            return;
        }
        setRecharging(true);
        try {
            const { data: orderRes } = await api.post('/payments/wallet/recharge/order', {
                amount_inr: Number(amountInr),
            });
            const rzp = new window.Razorpay({
                key: orderRes.key_id,
                amount: orderRes.amount,
                currency: orderRes.currency,
                name: 'AstroConsult',
                description: `Wallet recharge ₹${amountInr}`,
                order_id: orderRes.order_id,
                theme: { color: '#f59e0b' },
                // Round 48g — Minimal config for maximum iOS Safari
                // compatibility. Rounds 47d + 48f (custom `method:`
                // whitelist and `display.blocks` with
                // `show_default_blocks: false`) both suppressed UPI on
                // iOS Safari specifically (Chrome iOS rendered fine).
                // Removing the overrides lets Razorpay's own device
                // detection choose the correct payment method set —
                // which is the recommended path per their docs when
                // the merchant already has UPI enabled dashboard-side.
                // The `retry` block lets users try a different method
                // if their first attempt fails without closing the sheet.
                retry: { enabled: true, max_count: 3 },
                handler: async (response) => {
                    try {
                        const { data: vres } = await api.post('/payments/wallet/recharge/verify', {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                        });
                        const bonusInr = Number(vres.bonus_inr || 0);
                        const totalCredited = Number(vres.total_credited_inr ?? vres.amount_inr ?? amountInr);
                        const msg = bonusInr > 0
                            ? `Wallet topped up: ₹${totalCredited} credited (₹${bonusInr} bonus). New balance: ₹${vres.wallet_balance_inr}`
                            : `Wallet topped up. New balance: ₹${vres.wallet_balance_inr}`;
                        toast.success(msg);
                        setOpen(false);
                        if (onSuccessRef.current) {
                            try { onSuccessRef.current(vres.wallet_balance_inr ?? 0); } catch { /* noop */ }
                        }
                        onSuccessRef.current = null;
                    } catch (err) {
                        toast.error(readErr(err, 'Verification failed — please contact support'));
                    }
                },
                modal: { ondismiss: () => setRecharging(false) },
            });
            rzp.open();
        } catch (e) {
            toast.error(readErr(e, 'Could not start recharge'));
        } finally {
            setRecharging(false);
        }
    };

    // ── PayPal (USD) branch — Round 40i ──
    const doPaypalRecharge = async (amountUsd) => {
        if (!paypalConfig?.client_id) {
            toast.error('PayPal not configured — please contact support.');
            return;
        }
        setRecharging(true);
        try {
            // 1. Create the order on our backend.
            const { data: orderRes } = await api.post('/payments/paypal/order', {
                amount_usd: Number(amountUsd),
                purpose: 'wallet_recharge',
            });
            // 2. Load the PayPal JS SDK on-demand.
            const paypal = await loadPaypalSdk({ clientId: paypalConfig.client_id, currency: 'USD' });
            if (!paypal) throw new Error('PayPal SDK failed to load');

            // 3. Open the PayPal approval window using the JS SDK
            //    "Smart Buttons" Buttons API — but we drive it
            //    programmatically since we already have the order.
            //    Use the `Buttons` factory with createOrder returning
            //    our pre-created order_id.
            await new Promise((resolve, reject) => {
                // Render a hidden buttons element & click it programmatically.
                let mountNode = document.getElementById('paypal-mount-node');
                if (!mountNode) {
                    mountNode = document.createElement('div');
                    mountNode.id = 'paypal-mount-node';
                    mountNode.style.cssText = 'position:fixed;left:50%;top:50%;transform:translate(-50%,-50%);z-index:9999;background:white;padding:16px;border-radius:8px;box-shadow:0 8px 32px rgba(0,0,0,.4);min-width:300px;';
                    const header = document.createElement('div');
                    header.style.cssText = 'font-size:13px;color:#475569;margin-bottom:10px;text-align:center;';
                    header.textContent = `Pay $${Number(amountUsd).toFixed(2)} with PayPal`;
                    mountNode.appendChild(header);
                    const buttonsDiv = document.createElement('div');
                    buttonsDiv.id = 'paypal-buttons-container';
                    mountNode.appendChild(buttonsDiv);
                    const cancel = document.createElement('button');
                    cancel.textContent = 'Cancel';
                    cancel.style.cssText = 'margin-top:10px;background:transparent;border:0;color:#94a3b8;font-size:12px;cursor:pointer;width:100%;';
                    cancel.onclick = () => { try { document.body.removeChild(mountNode); } catch { /* noop */ } reject(new Error('cancelled')); };
                    mountNode.appendChild(cancel);
                    document.body.appendChild(mountNode);
                }
                paypal.Buttons({
                    style: { layout: 'vertical', shape: 'rect', color: 'gold', label: 'paypal' },
                    createOrder: () => orderRes.order_id,
                    onApprove: async (data) => {
                        try {
                            const { data: cap } = await api.post('/payments/paypal/order/capture', {
                                order_id: data.orderID,
                            });
                            try { document.body.removeChild(mountNode); } catch { /* noop */ }
                            const balance = cap.wallet_balance_inr ?? 0;
                            const bonusUsd = Number(orderRes.bonus_usd || 0);
                            const msg = bonusUsd > 0
                                ? `Wallet topped up: $${(orderRes.amount_usd + bonusUsd).toFixed(2)} credited ($${bonusUsd.toFixed(2)} bonus).`
                                : `Wallet topped up.`;
                            toast.success(msg);
                            setOpen(false);
                            if (onSuccessRef.current) {
                                try { onSuccessRef.current(balance); } catch { /* noop */ }
                            }
                            onSuccessRef.current = null;
                            resolve();
                        } catch (err) {
                            toast.error(readErr(err, 'Payment capture failed — we\u2019ll retry via webhook.'));
                            reject(err);
                        }
                    },
                    onError: (err) => {
                        toast.error('PayPal error — please try again.');
                        try { document.body.removeChild(mountNode); } catch { /* noop */ }
                        reject(err);
                    },
                    onCancel: () => {
                        try { document.body.removeChild(mountNode); } catch { /* noop */ }
                        reject(new Error('cancelled'));
                    },
                }).render('#paypal-buttons-container').catch(reject);
            });
        } catch (e) {
            if (e?.message !== 'cancelled') {
                toast.error(readErr(e, 'Could not start PayPal recharge'));
            }
        } finally {
            setRecharging(false);
        }
    };

    const doRecharge = async (amount) => {
        const min = region.currency === 'USD' ? 3 : 25;
        if (!amount || Number(amount) < min) {
            toast.error(`Minimum recharge is ${region.symbol}${min}.`);
            return;
        }
        if (region.currency === 'USD') return doPaypalRecharge(amount);
        return doRazorpayRecharge(amount);
    };

    return (
        <RechargeContext.Provider value={{ open: openRecharge, close: closeRecharge, region }}>
            {children}
            <RechargeSheet
                open={open}
                onOpenChange={(v) => { if (!v) closeRecharge(); else setOpen(true); }}
                region={region}
                tiers={tiers}
                bonusBrackets={bonusBrackets}
                customAmount={customAmount}
                setCustomAmount={setCustomAmount}
                onRecharge={doRecharge}
                recharging={recharging}
            />
        </RechargeContext.Provider>
    );
}

export function useRecharge() {
    const ctx = useContext(RechargeContext);
    if (!ctx) return { open: () => {}, close: () => {}, region: { currency: 'INR', symbol: '₹' } };
    return ctx;
}

function RechargeSheet({ open, onOpenChange, region, tiers, bonusBrackets, customAmount, setCustomAmount, onRecharge, recharging }) {
    const customPct = bonusPctFor(Number(customAmount), bonusBrackets, region.currency);
    const customBonus = Math.round((Number(customAmount) || 0) * customPct) / 100;
    const isUsd = region.currency === 'USD';
    const min = isUsd ? 3 : 25;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="shared-recharge-sheet">
                <DialogHeader>
                    <DialogTitle>Recharge wallet</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div
                        data-testid="shared-recharge-bonus-summary"
                        className="rounded-lg border border-marigold/30 bg-marigold/5 px-3 py-2 text-xs text-marigold/90 leading-relaxed"
                    >
                        <div className="font-medium text-marigold mb-0.5">Bonus on every recharge</div>
                        <div className="text-slate-300">
                            {isUsd ? (
                                <>
                                    $10–$50 <span className="text-marigold">+15%</span>
                                    <span className="text-slate-600 mx-1">·</span>
                                    $51–$150 <span className="text-marigold">+30%</span>
                                    <span className="text-slate-600 mx-1">·</span>
                                    Above $150 <span className="text-marigold">+50%</span>
                                </>
                            ) : (
                                <>
                                    ₹100–₹500 <span className="text-marigold">+15%</span>
                                    <span className="text-slate-600 mx-1">·</span>
                                    ₹501–₹1,000 <span className="text-marigold">+30%</span>
                                    <span className="text-slate-600 mx-1">·</span>
                                    Above ₹1,000 <span className="text-marigold">+50%</span>
                                </>
                            )}
                        </div>
                    </div>
                    {isUsd && (
                        <div className="text-[11px] text-slate-500 italic flex items-center gap-1">
                            Powered by PayPal · pay in your local currency at checkout
                        </div>
                    )}
                    <div className="grid grid-cols-3 gap-2">
                        {tiers.map((t) => {
                            const pct = bonusPctFor(t, bonusBrackets, region.currency);
                            const bonus = Math.round(t * pct) / 100;
                            return (
                                <button
                                    key={t}
                                    disabled={recharging}
                                    onClick={() => onRecharge(t)}
                                    data-testid={`shared-recharge-tier-${t}`}
                                    className="relative px-3 py-3 rounded-lg border border-marigold/30 hover:border-marigold hover:bg-marigold/10 text-parchment text-sm transition-colors disabled:opacity-50"
                                >
                                    <div>{formatPrice(t, region.currency)}</div>
                                    {pct > 0 && (
                                        <div className="mt-0.5 text-[10px] text-emerald-300 font-medium">
                                            +{formatPrice(bonus, region.currency)} bonus
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    <div>
                        <div className="text-xs text-slate-400 mb-1">
                            Or enter a custom amount (min {formatPrice(min, region.currency)})
                        </div>
                        <div className="flex gap-2">
                            <Input
                                type="number"
                                min={min}
                                value={customAmount}
                                onChange={(e) => setCustomAmount(e.target.value)}
                                placeholder={`Amount in ${region.symbol}`}
                                data-testid="shared-recharge-custom-input"
                            />
                            <Button
                                onClick={() => onRecharge(Number(customAmount))}
                                disabled={recharging || !customAmount || Number(customAmount) < min}
                                data-testid="shared-recharge-custom-btn"
                                className="bg-marigold text-obsidian"
                            >
                                {recharging ? <Loader2 className="animate-spin" size={14} /> : 'Recharge'}
                            </Button>
                        </div>
                        {customPct > 0 && (
                            <div className="mt-1.5 text-xs text-emerald-300">
                                You&apos;ll get {formatPrice(customBonus, region.currency)} bonus ({customPct}% extra)
                            </div>
                        )}
                    </div>
                    <button
                        onClick={() => onOpenChange(false)}
                        data-testid="shared-recharge-cancel"
                        className="w-full text-xs text-slate-400 hover:text-parchment"
                    >
                        Maybe later
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
